import { AlertTriangle, Calendar, FileText, ListChecks, Stethoscope } from 'lucide-react';

type CompositeReportSource = {
  resultId?: string;
  testKey?: string;
  label?: string;
  assessmentDate?: string;
  primaryMetric?: string;
  severity?: string;
  domain?: string;
  priority?: 'alta' | 'moderada' | 'contextual' | string;
  evidence?: string;
  clinicalMeaning?: string;
  implication?: string;
};

type CompositeReportPayload = {
  title?: string;
  clinicalSummary?: string;
  mainFindings?: string[];
  integratedInterpretation?: string;
  attentionPoints?: string[];
  limitations?: string;
  finalText?: string;
  sourceResults?: CompositeReportSource[];
};

type CompositeReportRendererProps = {
  payload: CompositeReportPayload;
};

const getPriorityLabel = (priority?: string): string => {
  switch (priority) {
    case 'alta':
      return 'Alta relevância';
    case 'moderada':
      return 'Relevância moderada';
    default:
      return 'Contextual';
  }
};

const getPriorityTone = (priority?: string): string => {
  switch (priority) {
    case 'alta':
      return 'bg-rose-50 text-rose-700 ring-1 ring-rose-200';
    case 'moderada':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
    default:
      return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
  }
};

const getPriorityRank = (priority?: string): number => {
  switch (priority) {
    case 'alta':
      return 3;
    case 'moderada':
      return 2;
    default:
      return 1;
  }
};

const renderMultilineText = (text?: string) => (
  <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
    {text?.trim() || 'Sem conteúdo registrado.'}
  </div>
);

const renderList = (items?: string[]) => {
  const normalizedItems = (items ?? []).map((item) => item.trim()).filter(Boolean);

  if (!normalizedItems.length) {
    return <div className="text-sm text-slate-500">Nenhum item registrado.</div>;
  }

  return (
    <ul className="space-y-2">
      {normalizedItems.map((item, index) => (
        <li key={`${item}-${index}`} className="rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
          {item}
        </li>
      ))}
    </ul>
  );
};

export default function CompositeReportRenderer({ payload }: CompositeReportRendererProps) {
  const sourceResults = Array.isArray(payload.sourceResults) ? payload.sourceResults : [];
  const sortedSourceResults = [...sourceResults].sort((a, b) => getPriorityRank(b.priority) - getPriorityRank(a.priority));
  const coveredDomains = [...new Set(sortedSourceResults.map((source) => source.domain?.trim()).filter(Boolean))];
  const highPriorityCount = sortedSourceResults.filter((source) => source.priority === 'alta').length;
  const prioritizedCount = sortedSourceResults.filter((source) => source.priority === 'alta' || source.priority === 'moderada').length;
  const dates = sortedSourceResults
    .map((source) => (source.assessmentDate ? new Date(source.assessmentDate) : null))
    .filter((value): value is Date => value instanceof Date && !Number.isNaN(value.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  const periodLabel =
    dates.length === 0
      ? 'Sem período'
      : dates.length === 1 || dates[0].toLocaleDateString('pt-BR') === dates[dates.length - 1].toLocaleDateString('pt-BR')
        ? dates[0].toLocaleDateString('pt-BR')
        : `${dates[0].toLocaleDateString('pt-BR')} a ${dates[dates.length - 1].toLocaleDateString('pt-BR')}`;

  return (
    <div className="space-y-5">
      <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-primary-50 p-3 text-primary-700">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Relatório integrado</div>
            <h4 className="mt-1 text-xl font-bold text-slate-900">{payload.title || 'Relatório de avaliações'}</h4>
            <p className="mt-1 text-sm text-slate-500">
              Conteúdo gerado como rascunho e salvo como avaliação V2 para revisão clínica.
            </p>
          </div>
        </div>
      </div>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[20px] border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            <Calendar className="h-4 w-4" />
            Período
          </div>
          <div className="mt-3 text-lg font-bold text-slate-900">{periodLabel}</div>
        </div>
        <div className="rounded-[20px] border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            <ListChecks className="h-4 w-4" />
            Avaliações
          </div>
          <div className="mt-3 text-lg font-bold text-slate-900">{sortedSourceResults.length}</div>
        </div>
        <div className="rounded-[20px] border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            <Stethoscope className="h-4 w-4" />
            Domínios
          </div>
          <div className="mt-3 text-sm font-semibold leading-relaxed text-slate-900">
            {coveredDomains.length > 0 ? coveredDomains.join(', ') : 'Não informados'}
          </div>
        </div>
        <div className="rounded-[20px] border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            <AlertTriangle className="h-4 w-4" />
            Prioridade clínica
          </div>
          <div className="mt-3 text-lg font-bold text-slate-900">{highPriorityCount}</div>
          <div className="mt-1 text-xs text-slate-500">
            {prioritizedCount} achado(s) com necessidade de atenção clínica
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Resumo clínico</div>
          {renderMultilineText(payload.clinicalSummary)}
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Interpretação integrada</div>
          {renderMultilineText(payload.integratedInterpretation)}
        </section>
      </div>

      <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-slate-400">
          <AlertTriangle className="h-4 w-4" />
          Achados clinicamente relevantes
        </div>
        {sortedSourceResults.length === 0 ? (
          <div className="text-sm text-slate-500">Nenhuma avaliação vinculada.</div>
        ) : (
          <div className="space-y-3">
            {sortedSourceResults.map((source, index) => (
              <div key={source.resultId || `${source.testKey}-${index}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-slate-900">{source.label || source.testKey || 'Avaliação'}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {source.assessmentDate ? new Date(source.assessmentDate).toLocaleString('pt-BR') : 'Sem data'}
                      {source.domain ? ` • ${source.domain}` : ''}
                    </div>
                  </div>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getPriorityTone(source.priority)}`}>
                    {getPriorityLabel(source.priority)}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Evidência</div>
                    <div className="mt-1 text-sm font-medium text-slate-900">
                      {source.evidence || [source.primaryMetric, source.severity].filter(Boolean).join(' • ') || 'Sem evidência estruturada'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Classificação</div>
                    <div className="mt-1 text-sm text-slate-700">{source.severity || 'Sem classificação'}</div>
                  </div>
                </div>
                <div className="mt-3 text-sm leading-relaxed text-slate-700">
                  {source.clinicalMeaning || 'Sem interpretação clínica estruturada registrada para este item.'}
                </div>
                <div className="mt-2 text-sm leading-relaxed text-slate-600">
                  <span className="font-semibold text-slate-800">Implicação clínica:</span> {source.implication || 'Sem implicação clínica registrada.'}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Texto do laudo</div>
        {renderMultilineText(payload.finalText)}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Principais achados</div>
          {renderList(payload.mainFindings)}
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Pontos de atenção</div>
          {renderList(payload.attentionPoints)}
        </section>
      </div>

      <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-slate-400">
          <ListChecks className="h-4 w-4" />
          Avaliações incluídas
        </div>
        {sortedSourceResults.length === 0 ? (
          <div className="text-sm text-slate-500">Nenhuma avaliação vinculada.</div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <div className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_130px_150px_130px] gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 md:grid">
              <div>Avaliação</div>
              <div>Domínio</div>
              <div>Resultado</div>
              <div>Classificação</div>
              <div>Relevância</div>
            </div>
            {sortedSourceResults.map((source, index) => (
              <div key={source.resultId || `${source.testKey}-${index}`} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_130px_150px_130px]">
                <div>
                  <div className="font-semibold text-slate-900">{source.label || source.testKey || 'Avaliação'}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {source.assessmentDate ? new Date(source.assessmentDate).toLocaleString('pt-BR') : 'Sem data'}
                  </div>
                </div>
                <div className="text-slate-600">{source.domain || '-'}</div>
                <div className="font-semibold text-slate-900">{source.primaryMetric || '-'}</div>
                <div className="text-slate-600">{source.severity || 'Sem classificação'}</div>
                <div>
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getPriorityTone(source.priority)}`}>
                    {getPriorityLabel(source.priority)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-slate-400">Limitações</div>
        {renderMultilineText(payload.limitations)}
      </section>
    </div>
  );
}
